import type React from "react";

/**
 * Read-only renderer for an authored grammar string. Swappable words are stored as `{{word}}`
 * tokens (see GrammarFocusUnderlineEditor); here they are shown as an underlined word with the
 * braces stripped, so non-authors never see the raw `{{ }}`. Plain text (and `\n`) passes through
 * unchanged, so it is safe to use on any limit/focus string, including word lists with no tokens.
 */
const TOKEN_SPLIT = /(\{\{.*?\}\})/g;
const TOKEN_MATCH = /^\{\{([\s\S]*)\}\}$/;
// Matches the swappable-word underline used in the authoring editor.
const UNDERLINE_STYLE: React.CSSProperties = { borderBottom: "2px solid #6366f1" };

interface UnderlinedTokensProps {
  text: string;
}

export function UnderlinedTokens({ text }: UnderlinedTokensProps) {
  const lines = text.split("\n");
  return (
    <>
      {lines.map((line, lineIdx) => (
        // Line index is a stable key here: the source string is fixed per render.
        // biome-ignore lint/suspicious/noArrayIndexKey: positional line content
        <span key={lineIdx}>
          {lineIdx > 0 && <br />}
          {line
            .split(TOKEN_SPLIT)
            .filter((part) => part !== "")
            .map((part, partIdx) => {
              const match = part.match(TOKEN_MATCH);
              return match ? (
                // biome-ignore lint/suspicious/noArrayIndexKey: positional token content
                <span key={partIdx} style={UNDERLINE_STYLE}>
                  {match[1]}
                </span>
              ) : (
                // biome-ignore lint/suspicious/noArrayIndexKey: positional text content
                <span key={partIdx}>{part}</span>
              );
            })}
        </span>
      ))}
    </>
  );
}
